import { queryString } from '@olivierpascal/helpers';

import type { IMaybeAsync } from '~/helpers/types';
import type { ApiClient, IApiClientAuthorization } from '~/lib/api/ApiClient';
import type { IApiLogInResponseBodyData } from '~/lib/api/modules/Auth';
import type { IApiGetUserResponseBody } from '~/lib/api/modules/Users';
import type { IAppRegion } from '~/lib/appSettings';
import type { IStorage, IStorageLocation } from '~/lib/storage';
import type { IAuthCacheEntry } from './cache';
import {
  getCurrentLocation,
  getNavigationUrl,
  getRegionFromPath,
} from '~/components/utils/Navigation';
import { KeyManifest, storageFactory } from '~/lib/storage';
import { AuthCacheManager } from './cache';
import { AuthCacheKey } from './cache/AuthCacheKey';
import { generateSessionId } from './generateSessionId';

/**
 * User info returned from the authentication API.
 */
export type IAuthUser = IApiGetUserResponseBody;

export type IAuthClientOptions = {
  /**
   * The location to use when storing cache data. The default setting is
   * `memory`.
   */
  cacheLocation?: IStorageLocation;

  /**
   * Specify a custom cache implementation to use for token storage and
   * retrieval. This setting takes precedence over `cacheLocation` if they are
   * both specified.
   */
  storage?: IStorage;
};

export type IAuthNavigateToLogInOptions = {
  /**
   * The URL to redirect to after a successful log-in.
   */
  nextUrl?: string;

  appRegion?: string;

  /**
   * Used to control the redirect and not rely on the lib to do the actual
   * redirect.
   */
  openUrl?: (url: string) => IMaybeAsync<void>;

  hasMultiSessions?: boolean;
};

export type IAuthCredentials = {
  email: string;
  password: string;
};

export type IAuthAsUser = {
  email?: string;
  id?: string;
};

export type IAuthLogOutOptions = {
  sessionIds?: Array<string>;

  /**
   * The URL to redirect to after a successful log-out.
   */
  nextUrl?: string;

  /**
   * Used to control the redirect and not rely on the lib to do the actual
   * redirect. If set to `false`, the lib will not perform the redirect.
   */
  openUrl?: ((url: string) => IMaybeAsync<void>) | false;
};

/**
 * The Auth Client is used to manage the authentication state and perform
 * authentication-related actions scoped to a client ID.
 */
export class AuthClient {
  private readonly storage: IStorage;
  private readonly options: IAuthClientOptions;
  private readonly cacheManager: AuthCacheManager<IAuthUser>;
  private readonly defaultOptions: Partial<IAuthClientOptions> = {};

  public constructor(
    public readonly clientId: string,
    private readonly apiClient: ApiClient,
    options?: IAuthClientOptions,
  ) {
    this.options = {
      ...this.defaultOptions,
      ...options,
    };

    this.storage =
      this.options.storage ??
      storageFactory(this.options.cacheLocation ?? 'memory')({
        namespace: 'auth',
      });

    this.cacheManager = new AuthCacheManager(
      this.storage,
      !this.storage.allKeys
        ? new KeyManifest(this.storage, this.clientId)
        : undefined,
    );
  }

  public async getAllCredentials(): Promise<Array<IAuthCacheEntry<IAuthUser>>> {
    const keys = this.storage.allKeys?.();
    if (!keys) {
      return [];
    }

    const allCredentials = (
      await Promise.all(
        keys.map((key) => this.cacheManager.get(AuthCacheKey.fromString(key))),
      )
    ).filter((credential) => !!credential?.user._id) as Array<
      IAuthCacheEntry<IAuthUser>
    >;

    return allCredentials;
  }

  public async restoreFromCache(
    sessionId: string,
  ): Promise<IAuthCacheEntry<IAuthUser> | undefined> {
    const credentials = await this.getCredentialsFromCache(sessionId);
    if (!credentials) {
      return undefined;
    }

    this.apiClient.setAuthorization({
      sessionId,
      region: credentials.region,
      authToken: credentials.authToken,
      user: credentials.user,
    });

    const currentUrl = new URL(getCurrentLocation());
    const pathname = currentUrl.pathname;
    const splittedPath = pathname.split('/');
    const appRegionFromPath = getRegionFromPath(splittedPath[1]);
    const isSameRegion =
      !appRegionFromPath || appRegionFromPath === credentials.region;
    if (!isSameRegion) {
      window.location.href = this.buildLogInUrl({
        nextUrl: `${pathname}${currentUrl.search}`,
        appRegion: appRegionFromPath,
        hasMultiSessions: true,
      });
    }

    return credentials;
  }

  private buildLogInUrl(options?: IAuthNavigateToLogInOptions): string {
    const { nextUrl, appRegion } = options ?? {};

    const path = options?.hasMultiSessions ? '^/sessions' : '^/login';
    const qs = queryString.build({ nextUrl, appRegion });
    const url = `${path}${qs ? `?${qs}` : ''}`;

    return getNavigationUrl(url, appRegion);
  }

  /**
   * Navigate to the log-in page.
   */
  public async navigateToLogIn(
    options?: IAuthNavigateToLogInOptions,
  ): Promise<void> {
    const url = this.buildLogInUrl(options);

    const { openUrl } = options ?? {};
    if (openUrl) {
      await openUrl(url);
    } else {
      window.location.assign(url);
    }
  }

  private async getAuthorizationFromApiResponse(
    region: IAppRegion,
    authData: IApiLogInResponseBodyData,
    extraFields?: Partial<IApiClientAuthorization>,
  ): Promise<IApiClientAuthorization> {
    const authToken = authData.authToken;
    const sessionId = generateSessionId(region, authData.userId);
    const authorization: IApiClientAuthorization = {
      sessionId,
      region,
      authToken,
      user: {
        ...authData.user,
        _id: authData.userId, // TODO [USER]: remove when API is ready
      },
      ...extraFields,
    };
    await this.setAuthorization(authorization);

    return authorization;
  }

  /**
   * Log in using the provided credentials.
   */
  public async logInWithCredentials(
    region: IAppRegion,
    credentials: IAuthCredentials,
  ): Promise<IApiClientAuthorization> {
    const response = await this.apiClient.auth.logIn(region, credentials);
    if (response.status !== 'success' || !response.data) {
      // TODO: throw error code
      throw new Error('Log-in failed.');
    }

    const authorization = await this.getAuthorizationFromApiResponse(
      region,
      response.data,
    );
    await this.setAuthorization(authorization);

    return authorization;
  }

  public async logInAs(
    region: IAppRegion,
    asUser: IAuthAsUser,
  ): Promise<IApiClientAuthorization> {
    const response = await this.apiClient.auth.logInAs(asUser);
    if (response.status !== 'success' || !response.data) {
      // TODO: throw error code
      throw new Error('Log-in as failed.');
    }

    const authorization = await this.getAuthorizationFromApiResponse(
      region,
      response.data,
      { isImpersonated: true },
    );
    await this.setAuthorization(authorization);

    return authorization;
  }

  public async setAuthorization(
    authorization: IApiClientAuthorization,
  ): Promise<void> {
    const sessionId = generateSessionId(
      authorization.region,
      authorization.user._id,
    );
    await this.cacheManager.set({
      clientId: this.clientId,
      sessionId,
      region: authorization.region,
      authToken: authorization.authToken,
      user: authorization.user,
      isImpersonated: authorization.isImpersonated,
    });

    this.apiClient.setAuthorization(authorization);
  }

  public async clearCredentials(sessionId?: string): Promise<void> {
    await this.cacheManager.clear(this.clientId, sessionId);
    this.apiClient.clearAuthorization();
  }

  /**
   * Returns the user information if available.
   */
  public async getUser(sessionId: string): Promise<IAuthUser | undefined> {
    const cache = await this.getCredentialsFromCache(sessionId);

    return cache?.user;
  }

  public async logOut(options?: IAuthLogOutOptions): Promise<void> {
    const { openUrl, sessionIds, nextUrl = '/' } = options ?? {};
    if (sessionIds?.length) {
      await Promise.all(
        sessionIds.map(async (sessionId) => {
          const authorization = await this.getCredentialsFromCache(sessionId);
          await this.apiClient.auth.logOut({ authorization });
          await this.clearCredentials(sessionId);
        }),
      );
    }

    if (openUrl !== false) {
      if (openUrl) {
        await openUrl(nextUrl);
      } else {
        window.location.assign(nextUrl);
      }
    }
  }

  public getCredentialsFromCache(
    sessionId: string,
  ): Promise<IAuthCacheEntry<IAuthUser> | undefined> {
    return this.cacheManager.get({
      clientId: this.clientId,
      sessionId,
    });
  }
}
